import React, { useState, useEffect, useRef } from 'react';
import {
  FiUsers,
  FiFileText,
  FiActivity,
  FiShield,
  FiMail,
  FiBell,
  FiUserCheck,
  FiTrendingUp,
  FiTrendingDown,
  FiRefreshCw,
  FiEye,
  FiClock,
  FiBarChart2,
  FiGrid
} from 'react-icons/fi';
import AxiosInstance from '../../AxiosInstance';
import config from '../../config/frontend.config';
import './Dashboard.css';

// Global flag to prevent duplicate dashboard API calls across the entire app
let globalDashboardFetching = false;

function Dashboard() {
  
  // Track component mounts
  const mountCount = useRef(0);
  mountCount.current += 1;
  
  const [dashboardData, setDashboardData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [isCached, setIsCached] = useState(false);
  const [refreshing, setRefreshing] = useState(false);
  const [isFetching, setIsFetching] = useState(false);

  // const userData = config.getUserData();
  // const userId = config.getUserId();

  useEffect(() => {
    
    // Simple approach - just call fetchDashboardData once
    fetchDashboardData();

    // Cleanup function
    return () => {
      setIsFetching(false);
    };
  }, []); // Empty dependency array - only run once on mount

  const fetchDashboardData = async (forceRefresh = false) => {
    
    // Global check to prevent any duplicate calls across the entire app
    if (globalDashboardFetching && !forceRefresh) {
      return;
    }

    // Prevent multiple simultaneous calls
    if (isFetching && !forceRefresh) {
      return;
    }

    try {
      globalDashboardFetching = true; // Set global flag
      setIsFetching(true);
      setLoading(true);
      setError(null);

      // Clear cache if force refresh
      if (forceRefresh) {
        setRefreshing(true);
        await AxiosInstance.post('/superadmin/dashboard/clear-cache');
      }

      // Try lightweight APIs first
      try {
        // Fetch data from separate lightweight APIs in parallel
        const [
          countsResponse,
          activityCountsResponse,
          notificationCountsResponse,
          invitationCountsResponse,
          recentActivitiesResponse
        ] = await Promise.all([
          AxiosInstance.get('/superadmin/counts'),
          AxiosInstance.get('/superadmin/activity-counts'),
          AxiosInstance.get('/superadmin/notification-counts'),
          AxiosInstance.get('/superadmin/invitation-counts'),
          AxiosInstance.get('/superadmin/recent-activities?limit=10')
        ]);

        // Check if all responses are successful
        if (countsResponse.data.statusCode === 200 &&
          activityCountsResponse.data.statusCode === 200 &&
          notificationCountsResponse.data.statusCode === 200 &&
          invitationCountsResponse.data.statusCode === 200 &&
          recentActivitiesResponse.data.statusCode === 200) {

          // Combine data from all APIs
          const combinedData = {
            counts: {
              ...countsResponse.data.data,
              totalActivities: activityCountsResponse.data.data.totalActivities,
              ...notificationCountsResponse.data.data,
              ...invitationCountsResponse.data.data,
              // Calculate inactive users
              inactiveUsers: countsResponse.data.data.totalUsers - (countsResponse.data.data.activeUsers || 0),
              // Set default for missing activeUsers field
              activeUsers: countsResponse.data.data.activeUsers || 0
            },
            analytics: {
              recentUserActivities: activityCountsResponse.data.data.recentUserActivities || [],
              topActiveUsers: activityCountsResponse.data.data.topActiveUsers || [],
              templatesByUser: [] // Not available in lightweight APIs
            },
            recentActivities: recentActivitiesResponse.data.data || []
          };

          setDashboardData(combinedData);
          setIsCached(false); // Separate APIs don't use cache
          return; // Success with lightweight APIs
        }
      } catch (lightweightError) {
          // Fallback to heavy API
      }

      // Fallback to heavy API if lightweight APIs fail
      try {
        const response = await AxiosInstance.get('/superadmin/dashboard');

        if (response.data.statusCode === 200) {
          setDashboardData(response.data.data);
          setIsCached(response.data.cached || false);
          return; // Success with heavy API
        }
      } catch (heavyError) {
        console.error('Heavy API also failed:', heavyError);
      }

      // If both approaches fail
      setError('Failed to load dashboard data. All services are unavailable.');

    } catch (err) {
      console.error('Error fetching dashboard data:', err);
      setError('Failed to load dashboard data. Please try again later.');
    } finally {
      setLoading(false);
      setRefreshing(false);
      setIsFetching(false);
      globalDashboardFetching = false; // Reset global flag
    }
  };

  const formatTime = (timestamp) => {
    const date = new Date(timestamp);
    const now = new Date();
    const diffInMinutes = Math.floor((now - date) / (1000 * 60));
    const diffInHours = Math.floor(diffInMinutes / 60);
    const diffInDays = Math.floor(diffInHours / 24);

    if (diffInMinutes < 1) return 'Just now';
    if (diffInMinutes < 60) return `${diffInMinutes}m ago`;
    if (diffInHours < 24) return `${diffInHours}h ago`;
    if (diffInDays < 7) return `${diffInDays}d ago`;

    return date.toLocaleDateString();
  };

  const getActionIcon = (action) => {
    switch (action?.toLowerCase()) {
      case 'create':
        return <FiFileText className="activity-icon create" />;
      case 'update':
        return <FiEye className="activity-icon update" />;
      case 'delete':
        return <FiUsers className="activity-icon delete" />;
      case 'login':
        return <FiUserCheck className="activity-icon login" />;
      default:
        return <FiActivity className="activity-icon default" />;
    }
  };

  // Always show the header, even if data is loading
  const displayTitle = "SuperAdmin Dashboard";

  if (error) {
    return (
      <div className="page-main">
        <div className="page-title-row">
          <FiGrid className="page-title-icon" size={28} />
          <h1>{displayTitle}</h1>
        </div>
        <div className="error-container">
          <div className="error-message">
            <p>{error}</p>
            <button onClick={() => fetchDashboardData(true)} className="retry-btn">
              <FiRefreshCw />
              Retry
            </button>
          </div>
        </div>
      </div>
    );
  }

  const { counts, analytics, recentActivities } = dashboardData || {};

  return (
    <div className="page-main">
      <div className="page-title-row">
        <h1>{displayTitle}</h1>
        <div className="dashboard-controls">
        <button
          onClick={() => fetchDashboardData(false)}
          className="refresh-btn"
          disabled={loading}
          title="Refresh"
        >
          <FiRefreshCw className={`refresh-icon ${loading ? 'spinning' : ''}`} />
        </button>
        </div>
      </div>

      {loading ? (
        <div className="loading-container">
          <div className="loading-spinner"></div>
          <p>Loading dashboard data...</p>
        </div>
        ) : (
          <>
          <div className="dashboard-overview">
            <div className="overview-card">
              <div className="card-icon users">
                <FiUsers />
              </div>
              <div className="card-content">
                <h3>{counts?.totalUsers || 0}</h3>
                <p>Total Users</p>
                <span className="card-subtitle">
                  {counts?.activeUsers || 0} active, {counts?.inactiveUsers || 0} inactive
                </span>
              </div>
            </div>

            {/* <div className="overview-card">
              <div className="card-icon superadmin">
                <FiShield />
              </div>
              <div className="card-content">
                <h3>{counts?.totalSuperadmins || 0}</h3>
                <p>Super Admins</p>
                <span className="card-subtitle">System administrators</span>
              </div>
            </div> */}

            <div className="overview-card">
              <div className="card-icon templates">
                <FiFileText />
              </div>
              <div className="card-content">
                <h3>{counts?.totalTemplates || 0}</h3>
                <p>Templates</p>
                <span className="card-subtitle">Email templates</span>
              </div>
            </div>

            <div className="overview-card">
              <div className="card-icon activities">
                <FiActivity />
              </div>
              <div className="card-content">
                <h3>{counts?.totalActivities || 0}</h3>
                <p>Activities</p>
                <span className="card-subtitle">System activities</span>
              </div>
            </div>

            <div className="overview-card">
              <div className="card-icon roles">
                <FiUserCheck />
              </div>
              <div className="card-content">
                <h3>{counts?.totalRoles || 0}</h3>
                <p>Roles</p>
                <span className="card-subtitle">User roles</span>
              </div>
            </div>

            <div className="overview-card">
              <div className="card-icon invitations">
                <FiMail />
              </div>
              <div className="card-content">
                <h3>{counts?.pendingInvitations || 0}</h3>
                <p>Pending Invites</p>
                <span className="card-subtitle">Awaiting response</span>
              </div>
            </div>

            <div className="overview-card">
              <div className="card-icon notifications">
                <FiBell />
              </div>
              <div className="card-content">
                <h3>{counts?.totalNotifications || 0}</h3>
                <p>Notifications</p>
                <span className="card-subtitle">
                  {counts?.unreadNotifications || 0} unread
                </span>
              </div>
            </div>
          </div>

          <div className="dashboard-analytics">
            <div className="analytics-section">
              <h3>Recent Activity Breakdown</h3>
              <div className="activity-stats">
                {analytics?.recentUserActivities?.length > 0 ? (
                  analytics.recentUserActivities.map((activity, index) => (
                    <div key={index} className="activity-stat">
                      {getActionIcon(activity._id)}
                      <div className="stat-content">
                        <h4>{activity._id}</h4>
                        <p>{activity.count} actions</p>
                      </div>
                    </div>
                  ))
                ) : (
                  <div className="no-data">
                    <p>No recent activity data available</p>
                  </div>
                )}
              </div>
            </div>

            <div className="analytics-section">
              <h3>Top Active Users</h3>
              <div className="user-stats">
                {analytics?.topActiveUsers?.length > 0 ? (
                  analytics.topActiveUsers.map((userStat, index) => (
                    <div key={index} className="user-stat">
                      <div className="user-rank">#{index + 1}</div>
                      <div className="user-info">
                        <h4>{userStat.username || `User ${userStat._id}`}</h4>
                        <p>{userStat.count} activities</p>
                      </div>
                      <div className="activity-count">
                        <FiTrendingUp />
                      </div>
                    </div>
                  ))
                ) : (
                  <div className="no-data">
                    <p>No active users data available</p>
                  </div>
                )}
              </div>
            </div>
          </div>

          {/* Recent Activities */}
          <div className="recent-activities-section">
            <h3>Recent System Activities</h3>
            <div className="activities-list">
              {recentActivities?.slice(0, 10).map((activity, index) => (
                <div key={index} className="activity-item">
                  {getActionIcon(activity.Action)}
                  <div className="activity-content">
                    <div className="activity-header">
                      <h4>{activity.Activity}</h4>
                      <span className="activity-time">
                        <FiClock />
                        {formatTime(activity.createdAt)}
                      </span>
                    </div>
                    <div className="activity-details">
                      <span className="activity-action">{activity.Action}</span>
                      {activity.Entity && (
                        <span className="activity-entity">on {activity.Entity}</span>
                      )}
                      {activity.ActivityByUsername && (
                        <span className="activity-user">by {activity.ActivityByUsername}</span>
                      )}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
          </>
        )}
      </div>
  );
}

export default Dashboard;